import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const schema = z.object({
  name: z.string().min(2, "Please enter your name"),
  email: z.string().email("Enter a valid email"),
  company: z.string().optional(),
  message: z.string().min(10, "Tell us a little more (min 10 characters)"),
});

const Field = ({ label, error, children }) => (
  <div className="border-b border-white/20 focus-within:border-gold transition-colors pb-2">
    <label className="overline text-ivory/50 block mb-2">{label}</label>
    {children}
    {error && <p className="text-gold text-xs mt-2" role="alert">{error}</p>}
  </div>
);

const inputCls =
  "w-full bg-transparent text-ivory placeholder-ivory/25 text-lg font-serif focus:outline-none py-1";

export const Contact = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { name: "", email: "", company: "", message: "" } });

  const onSubmit = async (values) => {
    try {
      const { data } = await api.post("/contact", values);
      toast.success(data.message || "Message received. We'll be in touch.");
      reset();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong. Please try again.");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-navy text-ivory py-24 sm:py-32 grain relative overflow-hidden">
      <div className="relative mx-auto max-w-7xl px-6 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <p className="overline text-gold mb-6">Contact</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-8">
            Let&apos;s talk, in confidence.
          </h2>
          <p className="text-ivory/65 leading-relaxed max-w-md mb-10">
            Whether you&apos;re managing an active crisis or building long-term reputation, tell us where things stand.
            Every message is read personally and held in confidence.
          </p>
          <a href="mailto:info@parkcomms.com" data-testid="contact-email-link" className="font-serif text-2xl link-underline text-ivory">
            info@parkcomms.com
          </a>
        </div>

        <motion.form
          onSubmit={handleSubmit(onSubmit)}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7 space-y-10"
        >
          <div className="grid sm:grid-cols-2 gap-10">
            <Field label="Full name" error={errors.name?.message}>
              <input data-testid="contact-name" className={inputCls} placeholder="Jane Doe" {...register("name")} />
            </Field>
            <Field label="Email" error={errors.email?.message}>
              <input data-testid="contact-email" className={inputCls} placeholder="jane@company.com" {...register("email")} />
            </Field>
          </div>
          <Field label="Company / Organization" error={errors.company?.message}>
            <input data-testid="contact-company" className={inputCls} placeholder="Optional" {...register("company")} />
          </Field>
          <Field label="How can we help?" error={errors.message?.message}>
            <textarea data-testid="contact-message" rows={4} className={inputCls + " resize-none"} placeholder="Tell us about the situation…" {...register("message")} />
          </Field>
          <button
            type="submit"
            data-testid="contact-submit-button"
            disabled={isSubmitting}
            className="group inline-flex items-center gap-3 bg-gold hover:bg-ivory disabled:opacity-50 text-navy px-10 py-4 overline transition-colors"
          >
            {isSubmitting ? "Sending…" : "Send message"}
            <span className="transition-transform group-hover:translate-x-1">&rarr;</span>
          </button>
        </motion.form>
      </div>
    </section>
  );
};
